/* eslint-disable no-unused-vars */
import "./Notifications.scss";
import { useState, useEffect } from "react";
import { InputAdornment, TextField, useMediaQuery } from "@mui/material";
import ShowUser from "./../../assets/show-user.svg";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { NotificationsColumns } from "../../SourceData";
import MessageModals from "../../components/MessageModals/MessageModals";
import ar_EG from "antd/es/locale/ar_EG"; // Arabic locale
import { ConfigProvider, DatePicker } from "antd";
import AddGif from "./../../assets/add-gif.gif";
import BlueButton from "../../components/BlueButton/BlueButton";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import api from "../../utils/api";
import dayjs from "dayjs";
import "dayjs/locale/ar"; // Import Arabic locale for dayjs
import { toast } from "react-toastify";
import { usePermissions } from "./../../hooks/usePermissions";

const { RangePicker } = DatePicker;

const Notifications = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");

  // State variables
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [currentModalView, setCurrentModalView] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Notifications data state
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  dayjs.locale("ar");

  const handleOpen = (view, notification = null) => {
    // Check permissions based on the view
    if (view === "createNotification" && !hasPermission(79)) {
      toast.error("ليس لديك صلاحية لإضافة الإشعارات");
      return;
    }
    if (view === "viewNotification" && !hasPermission(81)) {
      toast.error("ليس لديك صلاحية لعرض تفاصيل الإشعار");
      return;
    }

    setCurrentModalView(view);
    if (notification) {
      setSelectedNotification(notification);
    }
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedNotification(null);
  };

  const handleAddNotificationClick = () => {
    // Check add permission
    if (!hasPermission(79)) {
      toast.error("ليس لديك صلاحية لإضافة الإشعارات");
      return;
    }
  };

  const handleViewNotificationClick = (notification) => {
    // Check view notification permission
    if (!hasPermission(81)) {
      toast.error("ليس لديك صلاحية لعرض تفاصيل الإشعار");
      return;
    }

    handleOpen("viewNotification", notification);
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    // Reset to first page when filters change
    setPage(1);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Only update searchTerm if query is empty or has more than 2 characters
    if (value === "" || value.length > 2) {
      setSearchTerm(value);
      // Reset to first page when filters change
      setPage(1);
    }
  };

  // Fetch notifications data
  const fetchNotifications = async () => {
    // Check view permission before fetching data
    if (!hasPermission(80)) {
      return;
    }

    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Add search term if it's not empty
      if (searchTerm) {
        params.q = searchTerm;
      }

      // Add date range parameters if selected
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format("YYYY-MM-DD");
        params.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      const response = await api.get("/notifications", { params });

      if (response.data.status === "success") {
        setNotifications(response.data.data);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("فشل في جلب الإشعارات. يرجى المحاولة مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch notifications when dependencies change
  useEffect(() => {
    fetchNotifications();
  }, [page, pageSize, searchTerm, dateRange]);

  const actionColumn = [
    {
      field: "action",
      headerName: "",
      width: 160,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const canViewNotification = hasPermission(81);

        return (
          <div className="cell-action">
            <div
              className="cell-btn"
              onClick={() => handleViewNotificationClick(params.row)}
              style={{
                cursor: canViewNotification ? "pointer" : "not-allowed",
                opacity: canViewNotification ? 1 : 0.5,
              }}
              title={
                canViewNotification
                  ? "عرض تفاصيل الإشعار"
                  : "ليس لديك صلاحية لعرض تفاصيل الإشعار"
              }
            >
              <img src={ShowUser} alt="عرض" />
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="users">
      <div className="special-header">
        <p className="title">
          الاشعارات <span className="count-badge">{totalCount || 0}</span>
        </p>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="ابحث بعنوان الإشعار أو النص"
              onChange={handleSearchChange}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <ConfigProvider locale={ar_EG} direction="rtl">
              <RangePicker
                format={"DD MMM"}
                style={{
                  width: isMobile ? "100%" : isTablet ? "200px" : "300px",
                }}
                placeholder={["من", "الي"]}
                className="range-picker"
                onChange={handleDateRangeChange}
                value={dateRange}
                popupStyle={{ direction: "rtl" }}
                inputReadOnly={true}
              />
            </ConfigProvider>
          </div>

          {hasPermission(79) && (
            <BlueButton
              text="اضافة اشعار جديد"
              size={isMobile ? "full" : isTablet ? "fit" : "large"}
              variant="fill"
              icon={<img src={AddGif} alt="" />}
              path="/notifications/create"
              onClick={handleAddNotificationClick}
            />
          )}
        </div>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={NotificationsColumns.concat(actionColumn)}
          rows={notifications.map((notification, index) => ({
            ...notification,
            // Generate unique id if not present
            id: notification._id || index,
            // Add index for numbering
            index: (page - 1) * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            // Handle page change
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            // Handle page size change
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1); // Reset to first page when changing page size
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "إشعار في الصفحة",
            },
          }}
          rowHeight={80}
        />
      </div>

      {isOpen &&
        ((currentModalView === "createNotification" && hasPermission(79)) ||
          (currentModalView === "viewNotification" && hasPermission(81))) && (
          <SwipeableModals
            open={isOpen}
            onClose={handleClose}
            onOpen={handleOpen}
            currentView={currentModalView}
            selectedNotification={selectedNotification}
            refreshData={fetchNotifications}
          />
        )}
    </div>
  );
};

export default Notifications;
